import constants from "../constants";
import { getTranslation } from "../translate";

/**
 * Extract numeric Vimeo video id from id/URL strings.
 * Returns null when the value is not a recognizable Vimeo reference so we never
 * encode a retailer static page or other https URL as a "Vimeo" QR (CB-3284).
 */
function extractVimeoVideoId(videoIdOrUrl) {
  if (videoIdOrUrl == null || videoIdOrUrl === "") return null;
  const raw = String(videoIdOrUrl).trim();

  if (/^\d+$/.test(raw)) {
    return raw;
  }

  let m = raw.match(/(?:www\.)?vimeo\.com\/(?:video\/)?(\d+)/i);
  if (m) return m[1];

  m = raw.match(/player\.vimeo\.com\/video\/(\d+)/i);
  if (m) return m[1];

  return null;
}

/**
 * Canonical Vimeo player URL for poster QR codes (uses constants.VIMEO_PLAYER_URL).
 */
export function normalizeVimeoPageUrl(videoIdOrUrl) {
  const id = extractVimeoVideoId(videoIdOrUrl);
  if (!id) return null;
  return `${constants.VIMEO_PLAYER_URL}/${id}`;
}

/** @typedef {'food_service' | 'primal' | 'auto'} PosterMerchandisingSource */

/**
 * Which beef_product configuration row holds merchandising videos for this retailer (CB-3286).
 * Uses only `retailer.type` when set; otherwise `auto` tries food service then primal.
 */
export function getPosterMerchandisingSource(retailer) {
  const raw = retailer?.type;

  const asString = (v) => {
    if (v == null || v === "") return "";
    if (typeof v === "object") {
      return String(v.code ?? v.slug ?? v.name ?? v.id ?? "").trim();
    }
    return String(v).trim();
  };

  const t = asString(raw).toUpperCase();
  if (!t) return "auto";

  if (t === "FS" || t === "FOOD_SERVICE" || t.includes("FOOD SERVICE")) {
    return "food_service";
  }
  if (
    t === "RETAILER" ||
    t === "PACKER" ||
    t === "PRIMAL" ||
    t.includes("WHOLESALE")
  ) {
    return "primal";
  }
  if (t === "CORE") {
    return "auto";
  }

  return "auto";
}

function getTranslatedConfigRow(beefCut, languageCode, configFieldName) {
  let row = getTranslation(
    beefCut,
    { key: "languages_code", code: languageCode },
    configFieldName,
  );

  const list = beefCut?.[configFieldName];
  if (!row?.merchandising_videos?.length && Array.isArray(list)) {
    const fallback = list.find((r) => r?.merchandising_videos?.length);
    if (fallback) {
      row = fallback;
    }
  }

  return row;
}

function vimeoUrlFromMerchandisingVideos(videos) {
  if (!Array.isArray(videos) || videos.length === 0) {
    return null;
  }

  const vimeoLike = (v) =>
    String(v?.type ?? "").toUpperCase() === "VIMEO" ||
    /vimeo/i.test(String(v?.video_id ?? ""));

  const video = videos.find(vimeoLike);
  if (!video?.video_id) {
    return null;
  }

  return normalizeVimeoPageUrl(video.video_id);
}

/**
 * Public gateway origin for this retailer’s consumer site: `https://{subdomain}` only.
 */
export function getRetailerGatewayOrigin(retailer) {
  if (!retailer) return null;
  const sub = String(retailer.subdomain ?? "").trim();
  if (!sub) return null;
  const host = sub.replace(/^https?:\/\//i, "").split("/")[0];
  if (!host) return null;
  return `https://${host}`;
}

/**
 * Subprimal page on the retailer gateway when there is no merchandising video QR target.
 * Path: `/beef/subprimal/{beef_product_id}`.
 */
export function getPosterSubprimalUrl(beefCutId, retailer) {
  const origin = getRetailerGatewayOrigin(retailer);
  if (!origin || beefCutId == null || beefCutId === "") return null;
  const id = String(beefCutId).trim();
  if (!id) return null;
  return `${origin.replace(/\/$/, "")}/recipes/${id}`;
}

/**
 * Resolve poster beef-cut QR target: merchandising Vimeo when present, otherwise gateway subprimal page.
 * Uses food_service_configurations and/or primal_configurations based on getPosterMerchandisingSource.
 */
export function getPosterQrTargetUrl(
  beefCut,
  languageCode = "en-US",
  retailer,
) {
  const fromFoodService = () =>
    vimeoUrlFromMerchandisingVideos(
      getTranslatedConfigRow(
        beefCut,
        languageCode,
        "food_service_configurations",
      )?.merchandising_videos,
    );

  const fromPrimal = () =>
    vimeoUrlFromMerchandisingVideos(
      getTranslatedConfigRow(
        beefCut,
        languageCode,
        "primal_configurations",
      )?.merchandising_videos,
    );

  const source = getPosterMerchandisingSource(retailer);

  const vimeo =
    source === "primal"
      ? fromPrimal() ?? fromFoodService()
      : source === "food_service"
        ? fromFoodService() ?? fromPrimal()
        : fromFoodService() ?? fromPrimal();

  if (vimeo) return vimeo;

  return getPosterSubprimalUrl(beefCut?.id, retailer);
}
